// Passkey failures reach the person as calm copy, not DOMException text
// (owner, 09.10.2026: «The relying party ID is not a registrable domain
// suffix…» landed in the login banner on the phone). The ceremony fails in
// a handful of recognisable ways — map each to an auth-namespace key and let
// the raw name/message go to analytics only.
import type { ApiError } from '../../types/auth';
import { authErrorKey } from './authErrorCodes';

export type PasskeyFlow = 'login' | 'register';

type ErrorLike = Partial<Pick<ApiError, 'code' | 'status'>> & { name?: string };

/** True when the person dismissed the platform prompt — not a failure. */
export function isPasskeyCancelled(err: unknown): boolean {
  const name = (err as ErrorLike | null)?.name;
  return name === 'NotAllowedError' || name === 'AbortError';
}

/**
 * i18n key (auth namespace) for a failed passkey ceremony or API call.
 * Callers handle cancellation and the 404 «feature off» case before this.
 */
export function passkeyErrorKey(err: unknown, flow: PasskeyFlow): string {
  const e = (err ?? {}) as ErrorLike;
  // The browser refuses before any authenticator is involved: the RP ID the
  // server issued is not this page's domain, or the page is not https.
  if (e.name === 'SecurityError') return 'passkeyErrors.domainMismatch';
  if (e.name === 'NotSupportedError') return 'passkeyErrors.notSupported';
  // create() with a credential already on this authenticator.
  if (e.name === 'InvalidStateError' && flow === 'register') return 'passkeyErrors.alreadyRegistered';
  // Server-side refusals share the auth error-code contract (CORE-515/576).
  const apiKey = authErrorKey(e);
  if (apiKey) return apiKey;
  return flow === 'register' ? 'passkeyErrors.enrolFailed' : 'passkeyErrors.failed';
}
